import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  Bell, Calendar as CalendarIcon, Check, Clock, Euro,
  FileText, AlertTriangle, RefreshCw, Plus, Filter,
  ChevronRight, Wheat, Heart, Calculator, Users,
  Shield, Leaf, Settings, ArrowRight, X
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';

const CATEGORIES = {
  pac: { label: 'PAC', color: 'bg-green-100 text-green-800', icon: Euro },
  sanitaire: { label: 'Sanitaire', color: 'bg-pink-100 text-pink-800', icon: Heart },
  administratif: { label: 'Administratif', color: 'bg-orange-100 text-orange-800', icon: FileText },
  comptable: { label: 'Comptable', color: 'bg-blue-100 text-blue-800', icon: Calculator },
  culture: { label: 'Culture', color: 'bg-lime-100 text-lime-800', icon: Wheat },
  elevage: { label: 'Élevage', color: 'bg-amber-100 text-amber-800', icon: Heart },
  meteo: { label: 'Météo', color: 'bg-cyan-100 text-cyan-800', icon: Leaf }
};

const PRIORITES = {
  critique: { label: 'Critique', color: 'bg-red-500 text-white' },
  haute: { label: 'Haute', color: 'bg-orange-500 text-white' },
  normale: { label: 'Normale', color: 'bg-yellow-500 text-black' },
  info: { label: 'Info', color: 'bg-blue-500 text-white' }
};

export default function AlertesPACPage() {
  const navigate = useNavigate();
  const { activeSociete } = useWorkspace();
  const [alertes, setAlertes] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('toutes');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showGenererPAC, setShowGenererPAC] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newAlerte, setNewAlerte] = useState({
    type: 'custom',
    titre: '',
    description: '',
    categorie: 'administratif',
    priorite: 'normale'
  });
  const [reportAlerte, setReportAlerte] = useState(null);
  const [reportDate, setReportDate] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeSociete]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [alertesRes, dashRes] = await Promise.all([
        api.get('/alertes/'),
        api.get('/alertes/dashboard')
      ]);
      setAlertes(alertesRes.data.alertes || []);
      setDashboard(dashRes.data);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlerte = async () => {
    if (!newAlerte.titre || !selectedDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await api.post('/alertes/', {
        ...newAlerte,
        date_echeance: selectedDate.toISOString()
      });
      toast.success('Alerte créée avec succès');
      setShowNewDialog(false);
      setNewAlerte({ type: 'custom', titre: '', description: '', categorie: 'administratif', priorite: 'normale' });
      setSelectedDate(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleGenererPAC = async (annee) => {
    try {
      const res = await api.post(`/api/alertes/pac/generer/${annee}`);
      toast.success(res.data.message);
      setShowGenererPAC(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  const handleMarquerTraitee = async (alerteId) => {
    try {
      await api.post(`/api/alertes/${alerteId}/traiter`);
      toast.success('Alerte marquée comme traitée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    }
  };

  const handleReporter = async () => {
    if (!reportAlerte || !reportDate) return;
    
    try {
      await api.post(`/api/alertes/${reportAlerte.id}/reporter`, {
        nouvelle_date: reportDate.toISOString()
      });
      toast.success('Alerte reportée');
      setReportAlerte(null);
      setReportDate(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors du report');
    }
  };

  const getStatutBadge = (alerte) => {
    if (alerte.jours_restants < 0) {
      return <Badge className="bg-red-600 text-white">En retard</Badge>;
    } else if (alerte.jours_restants <= 3) {
      return <Badge className="bg-red-500 text-white">Urgent</Badge>;
    } else if (alerte.jours_restants <= 7) {
      return <Badge className="bg-orange-500 text-white">Proche</Badge>;
    } else if (alerte.jours_restants <= 30) {
      return <Badge className="bg-yellow-500 text-black">À planifier</Badge>;
    } else {
      return <Badge className="bg-blue-500 text-white">À venir</Badge>;
    }
  };

  const filteredAlertes = alertes.filter(a => {
    if (filterCategorie !== 'all' && a.categorie !== filterCategorie) return false;
    if (activeTab === 'urgentes' && a.jours_restants > 7) return false;
    if (activeTab === 'pac' && a.categorie !== 'pac') return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="alertes-pac-page">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-7 w-7 text-green-600" />
            Alertes & Échéances PAC
          </h1>
          <p className="text-gray-600">Gérez vos échéances PAC, rappels sanitaires et administratifs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowGenererPAC(true)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Générer PAC {new Date().getFullYear()}
          </Button>
          <Button onClick={() => setShowNewDialog(true)} data-testid="new-alert-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Alerte
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className={`${dashboard.en_retard > 0 ? 'border-red-500 bg-red-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">En retard</p>
                  <p className="text-3xl font-bold text-red-600">{dashboard.en_retard}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${dashboard.urgentes > 0 ? 'border-orange-500 bg-orange-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Urgentes (7j)</p>
                  <p className="text-3xl font-bold text-orange-600">{dashboard.urgentes}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total actives</p>
                  <p className="text-3xl font-bold text-gray-900">{dashboard.total_actives}</p>
                </div>
                <Bell className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">PAC</p>
                  <p className="text-3xl font-bold text-green-600">
                    {dashboard.par_categorie?.pac || 0}
                  </p>
                </div>
                <Euro className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Prochaines échéances */}
      {dashboard?.prochaines?.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-green-600" />
              Prochaines échéances
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {dashboard.prochaines.map((alerte) => (
                <div
                  key={alerte.id}
                  className="flex-shrink-0 w-64 p-3 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/alertes/${alerte.id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={CATEGORIES[alerte.categorie]?.color || 'bg-gray-100'}>
                      {CATEGORIES[alerte.categorie]?.label || alerte.categorie}
                    </Badge>
                    {getStatutBadge(alerte)}
                  </div>
                  <h4 className="font-medium text-gray-900 line-clamp-1">{alerte.titre}</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {format(new Date(alerte.date_echeance), 'dd MMM yyyy', { locale: fr })}
                    <span className="ml-2 font-medium">
                      ({alerte.jours_restants > 0 ? `J-${alerte.jours_restants}` : 'Passée'})
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des alertes */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
              <TabsList>
                <TabsTrigger value="toutes">Toutes</TabsTrigger>
                <TabsTrigger value="urgentes">Urgentes</TabsTrigger>
                <TabsTrigger value="pac">PAC</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select value={filterCategorie} onValueChange={setFilterCategorie}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredAlertes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Aucune alerte trouvée</p>
              <Button variant="link" onClick={() => setShowGenererPAC(true)}>
                Générer les alertes PAC
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAlertes.map((alerte) => {
                const CatIcon = CATEGORIES[alerte.categorie]?.icon || Bell;
                
                return (
                  <div
                    key={alerte.id}
                    className={`p-4 bg-white border rounded-lg hover:shadow-md transition-shadow ${
                      alerte.jours_restants < 0 ? 'border-red-300 bg-red-50' :
                      alerte.jours_restants <= 3 ? 'border-orange-300 bg-orange-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${CATEGORIES[alerte.categorie]?.color || 'bg-gray-100'}`}>
                          <CatIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{alerte.titre}</h4>
                          <p className="text-sm text-gray-500 mt-1">{alerte.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={CATEGORIES[alerte.categorie]?.color || 'bg-gray-100'}>
                              {CATEGORIES[alerte.categorie]?.label || alerte.categorie}
                            </Badge>
                            {getStatutBadge(alerte)}
                            <span className="text-sm text-gray-400">
                              {format(new Date(alerte.date_echeance), 'dd MMMM yyyy', { locale: fr })}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReportAlerte(alerte)}
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Reporter
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleMarquerTraitee(alerte.id)}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Traiter
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Nouvelle Alerte */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Alerte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Titre *</label>
              <Input
                value={newAlerte.titre}
                onChange={(e) => setNewAlerte({...newAlerte, titre: e.target.value})}
                placeholder="Ex: Déclaration TVA Q1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={newAlerte.description}
                onChange={(e) => setNewAlerte({...newAlerte, description: e.target.value})}
                placeholder="Détails de l'échéance..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Catégorie</label>
                <Select 
                  value={newAlerte.categorie} 
                  onValueChange={(v) => setNewAlerte({...newAlerte, categorie: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Priorité</label>
                <Select 
                  value={newAlerte.priorite} 
                  onValueChange={(v) => setNewAlerte({...newAlerte, priorite: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITES).map(([key, p]) => (
                      <SelectItem key={key} value={key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Date d'échéance *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button onClick={handleCreateAlerte}>Créer l'alerte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Générer PAC */}
      <Dialog open={showGenererPAC} onOpenChange={setShowGenererPAC}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-green-600" />
              Générer les alertes PAC
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Cela créera automatiquement toutes les échéances PAC pour l'année sélectionnée :
            </p>
            <ul className="text-sm text-gray-500 space-y-1 mb-4">
              <li>• Ouverture TelePAC (avril)</li>
              <li>• Déclaration PAC (mai)</li>
              <li>• Modification sans pénalité (mai)</li>
              <li>• Période de contrôles (juin)</li>
              <li>• Acompte PAC (octobre)</li>
              <li>• Solde PAC (décembre)</li>
            </ul>
            <div className="flex gap-2">
              <Button 
                className="flex-1" 
                variant="outline"
                onClick={() => handleGenererPAC(new Date().getFullYear())}
              >
                {new Date().getFullYear()}
              </Button>
              <Button 
                className="flex-1"
                onClick={() => handleGenererPAC(new Date().getFullYear() + 1)}
              >
                {new Date().getFullYear() + 1}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Reporter */}
      <Dialog open={!!reportAlerte} onOpenChange={() => setReportAlerte(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reporter l'alerte</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Reporter "{reportAlerte?.titre}" à une nouvelle date :
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {reportDate ? format(reportDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={reportDate}
                  onSelect={setReportDate}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button onClick={handleReporter} disabled={!reportDate}>Reporter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
